import React, { useState } from 'react';

const EverythingScienceShowcase = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [unlockedDepts, setUnlockedDepts] = useState(3);

  const departments = [
    { name: 'Computer Science', icon: '💻', color: 'from-blue-500 to-cyan-500' },
    { name: 'Biology', icon: '🧬', color: 'from-green-500 to-emerald-500' },
    { name: 'Chemistry', icon: '⚗️', color: 'from-purple-500 to-pink-500' },
    { name: 'Physics', icon: '⚛️', color: 'from-orange-500 to-amber-500' },
    { name: 'Mathematics', icon: '📐', color: 'from-indigo-500 to-blue-500' },
    { name: 'Earth Sciences', icon: '🌍', color: 'from-teal-500 to-green-500' },
    { name: 'Ocean Sciences', icon: '🌊', color: 'from-cyan-500 to-blue-500' },
    { name: 'Psychology', icon: '🧠', color: 'from-pink-500 to-rose-500' },
    { name: 'Biochemistry', icon: '🔬', color: 'from-violet-500 to-purple-500' }
  ];

  const tiers = [
    { name: 'Bronze', depts: 3, color: 'from-amber-600 to-amber-700', reward: 'Participation Badge' },
    { name: 'Silver', depts: 5, color: 'from-gray-300 to-gray-400', reward: 'Science Passport Stamp' },
    { name: 'Gold', depts: 7, color: 'from-yellow-400 to-amber-500', reward: 'Priority T-shirt Pickup' },
    { name: 'Platinum', depts: 9, color: 'from-cyan-300 to-blue-400', reward: 'Prize Draw Entry' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900 text-white">
      {/* Hero Section */}
      <header className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-500/20 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
            <span className="text-emerald-300 text-sm font-medium">📅 September 8, 2025</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
            Everything Science
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
            Digital passport platform for Memorial University's Faculty of Science orientation—
            gamifying department exploration for 500+ new students
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a
              href="https://everything-science.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-500 px-6 py-3 rounded-lg transition-all font-semibold shadow-lg shadow-emerald-500/30"
            >
              <span>Visit Frontend</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <a
              href="https://everything-science-backend-production.up.railway.app/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-rose-600 hover:bg-rose-500 px-6 py-3 rounded-lg transition-all font-semibold"
            >
              <span>Admin Dashboard</span>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-emerald-400">500+</div>
              <div className="text-gray-400 text-sm">Students</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-teal-400">9</div>
              <div className="text-gray-400 text-sm">Departments</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-cyan-400">4</div>
              <div className="text-gray-400 text-sm">Reward Tiers</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="text-3xl font-bold text-blue-400">2</div>
              <div className="text-gray-400 text-sm">Years Running</div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* How It Works */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📱</span>
              </div>
              <h3 className="font-bold text-white mb-2">1. Scan QR</h3>
              <p className="text-gray-400 text-sm">Visit a department booth and scan their unique QR code</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✍️</span>
              </div>
              <h3 className="font-bold text-white mb-2">2. Register Once</h3>
              <p className="text-gray-400 text-sm">Enter your name and MUN email on first visit</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎯</span>
              </div>
              <h3 className="font-bold text-white mb-2">3. Collect Stamps</h3>
              <p className="text-gray-400 text-sm">Visit more departments to unlock reward tiers</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🏆</span>
              </div>
              <h3 className="font-bold text-white mb-2">4. Win Prizes</h3>
              <p className="text-gray-400 text-sm">Reach Platinum for prize draw entry!</p>
            </div>
          </div>
        </section>

        {/* Interactive Demo */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-4">Your Science Passport</h2>
          <p className="text-gray-400 text-center mb-8">Click departments to simulate scanning</p>
          
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-400 mb-2">
                <span>Progress</span>
                <span>{unlockedDepts}/9 Departments</span>
              </div>
              <div className="h-4 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 transition-all duration-500"
                  style={{ width: `${(unlockedDepts / 9) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Tier Progress */}
            <div className="grid grid-cols-4 gap-4 mb-8">
              {tiers.map((tier, index) => (
                <div 
                  key={tier.name}
                  className={`rounded-lg p-4 text-center transition-all ${
                    unlockedDepts >= tier.depts 
                      ? `bg-gradient-to-br ${tier.color} shadow-lg` 
                      : 'bg-gray-800/50 opacity-50'
                  }`}
                >
                  <div className="font-bold">{tier.name}</div>
                  <div className="text-xs opacity-75">{tier.depts}+ depts</div>
                </div>
              ))}
            </div>

            {/* Departments Grid */}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
              {departments.map((dept, index) => (
                <button
                  key={dept.name}
                  onClick={() => setUnlockedDepts(Math.min(9, Math.max(unlockedDepts, index + 1)))}
                  className={`rounded-xl p-4 transition-all ${
                    index < unlockedDepts
                      ? `bg-gradient-to-br ${dept.color} shadow-lg scale-100`
                      : 'bg-gray-800/50 hover:bg-gray-700/50 opacity-60 hover:opacity-100'
                  }`}
                >
                  <div className="text-3xl mb-2">{dept.icon}</div>
                  <div className="text-xs font-medium">{dept.name}</div>
                  {index < unlockedDepts && (
                    <div className="text-xs mt-1 opacity-75">✓ Visited</div>
                  )}
                </button>
              ))}
            </div>

            {/* Reset Button */}
            <div className="text-center mt-6">
              <button
                onClick={() => setUnlockedDepts(0)}
                className="text-gray-400 hover:text-white text-sm underline"
              >
                Reset Demo
              </button>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Platform Features</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="text-xl font-bold text-emerald-400 mb-4">For Students</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-emerald-400 mr-2">✓</span>
                  <span>One-time simple registration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-400 mr-2">✓</span>
                  <span>Real-time progress tracking</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-400 mr-2">✓</span>
                  <span>Department info pages with programs & careers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-400 mr-2">✓</span>
                  <span>Progressive reward system</span>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-400 mr-2">✓</span>
                  <span>T-shirt eligibility tracking</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="text-xl font-bold text-rose-400 mb-4">For Administrators</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-rose-400 mr-2">✓</span>
                  <span>Real-time event monitoring</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-400 mr-2">✓</span>
                  <span>User management dashboard</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-400 mr-2">✓</span>
                  <span>Participation analytics</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-400 mr-2">✓</span>
                  <span>Prize draw management</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-400 mr-2">✓</span>
                  <span>Department traffic data</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Technology Stack</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="text-lg font-bold text-cyan-400 mb-4">Frontend (Netlify)</h3>
              <div className="flex flex-wrap gap-2">
                {['React', 'QR Scanner', 'Progressive Web App', 'Responsive Design'].map(tech => (
                  <span key={tech} className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="text-lg font-bold text-rose-400 mb-4">Backend (Railway)</h3>
              <div className="flex flex-wrap gap-2">
                {['Node.js', 'REST API', 'Admin Dashboard', 'Real-time Updates'].map(tech => (
                  <span key={tech} className="px-3 py-1 bg-rose-500/20 text-rose-300 rounded-full text-sm">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Role */}
        <section>
          <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">My Role</h3>
            <p className="text-lg text-gray-300 mb-6">
              Science Orientation Coordinator & Platform Developer
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-white/10 rounded-full">Memorial University Faculty of Science</span>
              <span className="px-4 py-2 bg-white/10 rounded-full">2024 & 2025 Orientations</span>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black/30 border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-4">
            Everything Science 2025 — Memorial University Faculty of Science
          </p>
          <div className="flex justify-center gap-6">
            <a
              href="https://everything-science.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Visit Platform →
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EverythingScienceShowcase;
